import { useEffect, useState } from "react";
import { masterApi, apiError } from "@/lib/api";
import { useUnit } from "@/pages/Master/MasterApp";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { modal } from "@/lib/modal";
import { inicioDiaBR, fimDiaBR } from "@/lib/periodoBR";
import { Star, Trophy, Medal } from "lucide-react";

const inputCls = "h-9 rounded-lg bg-white border border-neutral-200 px-3 text-xs outline-none focus:border-[color:var(--brand-blue)] focus:ring-2 focus:ring-blue-500/15 transition";

export default function MasterPesquisas() {
  const { unitId } = useUnit();
  const [items, setItems] = useState([]);
  const [resumo, setResumo] = useState(null);
  const [porFrentista, setPorFrentista] = useState([]);
  const [frentistas, setFrentistas] = useState([]);
  const [filters, setFilters] = useState({
    frentista_id: "", inicio: "", fim: "", nota_min: "", ofereceu: "",
  });

  useEffect(() => {
    masterApi.get("/frentistas").then((r) => setFrentistas(r.data)).catch(() => {});
  }, []);

  const buildQS = (extra = {}) => {
    const p = new URLSearchParams();
    if (unitId !== "all") p.set("unit_id", unitId);
    if (filters.frentista_id) p.set("frentista_id", filters.frentista_id);
    // Dia de Brasília (00:00 a 23:59:59.999999), não 00:00 UTC (= 21h do dia anterior): lib/periodoBR.
    if (inicioDiaBR(filters.inicio)) p.set("inicio", inicioDiaBR(filters.inicio));
    if (fimDiaBR(filters.fim)) p.set("fim", fimDiaBR(filters.fim));
    if (filters.nota_min) p.set("nota_min", filters.nota_min);
    if (filters.ofereceu !== "") p.set("ofereceu_servicos", filters.ofereceu);
    Object.entries(extra).forEach(([k, v]) => p.set(k, v));
    const qs = p.toString();
    return qs ? `?${qs}` : "";
  };

  const load = async () => {
    try {
      const [list, res, byF] = await Promise.all([
        masterApi.get(`/master/relatorios/pesquisas${buildQS()}`),
        masterApi.get(`/master/relatorios/pesquisas/resumo${unitId !== "all" ? `?unit_id=${unitId}` : ""}`),
        masterApi.get(`/master/relatorios/pesquisas/por-frentista${buildQS()}`),
      ]);
      setItems(list.data); setResumo(res.data); setPorFrentista(byF.data);
    } catch (e) { modal.error(apiError(e)); }
  };
  useEffect(() => { load(); }, [unitId, filters]);

  return (
    <div className="px-4 md:px-6 pt-4 pb-8 space-y-4 float-in" data-testid="master-pesquisas">
      <div>
        <div className="chip mb-1.5 text-[10px] py-0.5">Satisfação</div>
        <h1 className="font-serif-display text-2xl md:text-3xl leading-[1.1]">Pesquisas</h1>
      </div>

      {resumo && (
        <section className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Kpi label="Respostas" value={String(resumo.total)} />
          <Kpi label="Nota média" value={resumo.nota_media != null ? resumo.nota_media.toFixed(2) : "—"} accent />
          <Kpi label="Ofereceu serviços" value={`${resumo.ofereceu_pct}%`} />
          <Kpi label="5 estrelas" value={String(resumo.distribuicao?.[5] || 0)} />
        </section>
      )}

      {/* Ranking por vendedor */}
      <section className="card-soft rounded-xl overflow-hidden" data-testid="ranking-vendedores">
        <div className="px-4 py-3 flex items-center gap-2 border-b border-neutral-100">
          <Trophy size={14} className="text-amber-500" />
          <h2 className="font-serif-display text-lg">Ranking por Vendedor</h2>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-neutral-50">
                <TableHead className="text-[10px] uppercase tracking-widest">#</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest">Vendedor</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest">Resp.</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest">Nota Média</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest">Promotores (nota 4–5)</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest">Detratores (nota 1–2)</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest">Ofereceu Serviços</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest">Distribuição</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {porFrentista.map((f, idx) => (
                <TableRow key={f.frentista_id} data-testid={`vend-row-${f.frentista_id}`}>
                  <TableCell className="font-mono text-xs">
                    {idx < 3 ? <span className="inline-flex items-center gap-1"><Medal size={14} style={{ color: idx === 0 ? "#D4AF37" : idx === 1 ? "#9CA3AF" : "#CD7F32" }} />{idx + 1}º</span> : `#${idx + 1}`}
                  </TableCell>
                  <TableCell className="font-medium">{f.frentista_nome || "—"}</TableCell>
                  <TableCell>{f.respostas}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <span className="font-semibold">{f.nota_media}</span>
                      <Star size={12} className="text-amber-400 fill-amber-400" />
                    </div>
                  </TableCell>
                  <TableCell><span className="text-emerald-700 font-medium">{f.promotores_pct}%</span></TableCell>
                  <TableCell><span className="text-rose-700 font-medium">{f.detratores_pct}%</span></TableCell>
                  <TableCell>{f.ofereceu_pct}%</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-0.5">
                      {[5, 4, 3, 2, 1].map((n) => (
                        <span key={n} className="px-1 text-[9px] font-mono text-neutral-500">
                          {n}: {f.distribuicao[n] || 0}
                        </span>
                      ))}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {porFrentista.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-neutral-500 py-6">Sem respostas no período.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </section>

      {/* Filtros */}
      <section className="card-soft rounded-xl p-4 space-y-2" data-testid="filtros-pesquisa">
        <h3 className="font-serif-display text-base">Filtrar respostas</h3>
        <div className="grid md:grid-cols-5 gap-2">
          <select data-testid="f-frentista" value={filters.frentista_id}
                  onChange={(e) => setFilters({ ...filters, frentista_id: e.target.value })}
                  className={inputCls + " w-full"}>
            <option value="">Todos vendedores</option>
            {frentistas.filter(f => unitId === "all" || f.unit_id === unitId).map((f) => (
              <option key={f.id} value={f.id}>{f.nome}</option>
            ))}
          </select>
          <input type="date" data-testid="f-inicio" value={filters.inicio}
                 onChange={(e) => setFilters({ ...filters, inicio: e.target.value })}
                 className={inputCls + " w-full"} placeholder="Início" />
          <input type="date" data-testid="f-fim" value={filters.fim}
                 onChange={(e) => setFilters({ ...filters, fim: e.target.value })}
                 className={inputCls + " w-full"} placeholder="Fim" />
          <select data-testid="f-nota" value={filters.nota_min}
                  onChange={(e) => setFilters({ ...filters, nota_min: e.target.value })}
                  className={inputCls + " w-full"}>
            <option value="">Qualquer nota</option>
            <option value="5">5 estrelas</option>
            <option value="4">≥ 4 estrelas</option>
            <option value="3">≥ 3 estrelas</option>
            <option value="2">≥ 2 estrelas</option>
            <option value="1">≥ 1 estrela</option>
          </select>
          <select data-testid="f-ofereceu" value={filters.ofereceu}
                  onChange={(e) => setFilters({ ...filters, ofereceu: e.target.value })}
                  className={inputCls + " w-full"}>
            <option value="">Ofereceu? (todos)</option>
            <option value="true">Sim</option>
            <option value="false">Não</option>
          </select>
        </div>
        <button onClick={() => setFilters({ frentista_id: "", inicio: "", fim: "", nota_min: "", ofereceu: "" })}
                className="btn-ghost h-9 px-4 text-[11px] font-medium">
          Limpar Filtros
        </button>
      </section>

      <section className="card-soft rounded-xl overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-neutral-50">
              <TableHead className="text-[10px] uppercase tracking-widest">Quando</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Cliente</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Vendedor</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Nota</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Ofereceu</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((p) => (
              <TableRow key={p.atendimento_id} data-testid={`pesquisa-row-${p.atendimento_id}`}>
                <TableCell className="text-xs">{new Date(p.criado_em).toLocaleString("pt-BR")}</TableCell>
                <TableCell>{p.cliente_nome || "—"}</TableCell>
                <TableCell>{p.frentista_nome || "—"}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-0.5">
                    {[1, 2, 3, 4, 5].map((n) => <Star key={n} size={13} className={p.nota >= n ? "text-amber-400 fill-amber-400" : "text-neutral-300"} />)}
                  </div>
                </TableCell>
                <TableCell>
                  {p.ofereceu_servicos ? <span className="chip chip-green text-[10px] py-0.5">Sim</span>
                    : <span className="chip chip-red text-[10px] py-0.5">Não</span>}
                </TableCell>
              </TableRow>
            ))}
            {items.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-neutral-500 py-6">Nenhuma pesquisa no filtro aplicado.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </section>
    </div>
  );
}

function Kpi({ label, value, accent }) {
  return (
    <div className={`card-soft rounded-xl p-3.5 ${accent ? "bg-gradient-to-br from-amber-50 to-orange-50" : ""}`}>
      <div className="text-[10px] uppercase tracking-widest text-neutral-500">{label}</div>
      <div className="font-serif-display text-2xl mt-0.5">{value}</div>
    </div>
  );
}
